import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { DeliveryNoteOverviewModalComponent } from '../../shared/modals/delivery-note-overview-modal/delivery-note-overview-modal.component';
import { DeliveryStatusEnum, IDeliveryNote } from '../../shared/models/delivery-note.model';
import { IDailyDeliveredCount, IDashboardStats } from '../../shared/models/statistics.model';
import { DeliveryNoteService } from '../../shared/services/delivery-note.service';
import { StatisticService } from '../../shared/services/statistic.service';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, DeliveryNoteOverviewModalComponent],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit, OnDestroy {
  public stats: { title: string; value: string; icon: string }[] = [];
  public chartData: IDailyDeliveredCount[] = [];
  public isLoadingStats = false;
  public chartMaxCount = 1;

  public recentDeliveryNotes: IDeliveryNote[] = [];
  public isLoadingDeliveryNotes = false;

  public showOverviewModal = false;
  public selectedDeliveryNote: IDeliveryNote | null = null;
  public selectedOrderInfo = '';

  private subscriptions = new Subscription();

  constructor(
    private deliveryNoteService: DeliveryNoteService,
    private statisticService: StatisticService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadDashboardStats();
    this.loadRecentDeliveryNotes();
  }

  public loadDashboardStats(): void {
    this.isLoadingStats = true;
    this.subscriptions.add(
      this.statisticService.getDashboardData().subscribe({
        next: response => {
          if (response.DATA) {
            this.applyStats(response.DATA.stats);
            this.chartData = response.DATA.deliveredLast30Days;
            this.chartMaxCount = Math.max(...this.chartData.map(d => d.count), 1);
          }
          this.isLoadingStats = false;
        },
        error: () => {
          console.error('Error loading dashboard statistics');
          this.isLoadingStats = false;
        },
      })
    );
  }

  private applyStats(data: IDashboardStats): void {
    this.stats = [
      { title: 'Importierte Aufträge', value: data.ordersCreatedToday.toLocaleString('de-DE'), icon: 'inventory_2' },
      { title: 'Erstellte Lieferzettel', value: data.deliveryNotesCreatedToday.toLocaleString('de-DE'), icon: 'description' },
      { title: 'Abgeschlossene Lieferzettel', value: data.deliveredToday.toLocaleString('de-DE'), icon: 'local_shipping' },
      { title: 'Ausgelieferte Kartons', value: data.totalCartonsDeliveredToday.toLocaleString('de-DE'), icon: 'widgets' },
    ];
  }

  public formatChartDate(dateStr: string): string {
    const d = new Date(dateStr);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  }

  public getBarHeight(count: number): string {
    const pct = this.chartMaxCount > 0 ? (count / this.chartMaxCount) * 100 : 0;
    return Math.max(pct, 2) + '%';
  }

  public loadRecentDeliveryNotes(): void {
    this.isLoadingDeliveryNotes = true;
    this.subscriptions.add(
      this.deliveryNoteService.getDeliveryNotes(1, 3, '', 'updatedAt', 'desc').subscribe({
        next: response => {
          if (response.DATA) {
            this.recentDeliveryNotes = response.DATA.data;
          }
          this.isLoadingDeliveryNotes = false;
        },
        error: () => {
          console.error('Error loading recent delivery notes');
          this.isLoadingDeliveryNotes = false;
        },
      })
    );
  }

  public formatDate(date: Date | string | undefined): string {
    if (!date) return '—';
    const d = new Date(date);
    return d.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  public getStatusName(status: DeliveryStatusEnum): string {
    return this.deliveryNoteService.getStatusName(status);
  }

  public getStatusClass(status: DeliveryStatusEnum): string {
    return this.deliveryNoteService.getStatusClass(status);
  }

  public getPositionsCount(note: IDeliveryNote): number {
    return note.positions?.length || 0;
  }

  public openOverviewModal(note: IDeliveryNote): void {
    this.selectedDeliveryNote = note;
    this.selectedOrderInfo = note.order ? `${note.order.internalOrderId} - ${note.order.companyName}` : '';
    this.showOverviewModal = true;
  }

  public closeOverviewModal(): void {
    this.showOverviewModal = false;
    this.selectedDeliveryNote = null;
    this.selectedOrderInfo = '';
  }

  public onEditFromModal(note: IDeliveryNote): void {
    this.closeOverviewModal();
    this.router.navigate(['/delivery-notes/edit', note._id]);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
